/**
 * Data for the Hudl page: games, exportable fields, saved export layouts and
 * recent imports. A 402 from the Hudl service becomes `tierLimitError` so the
 * page can show the upgrade panel instead of controls that cannot work.
 */
import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { ApiError } from '../../lib/api-error';
import type {
  HudlExportFieldCatalog,
  HudlExportTemplate,
  HudlImportRecord,
  TeamEvent,
} from '../../lib/api-types';
import { TIER_LIMIT_ERROR_CODE } from '../../lib/constants';

import { sortEventsForExport, toMessage } from './hudl-format';

const EVENT_PAGE_SIZE = 50;
export const RECENT_IMPORT_LIMIT = 10;

export interface HudlPageData {
  events: TeamEvent[];
  fields: HudlExportFieldCatalog | null;
  templates: HudlExportTemplate[];
  imports: HudlImportRecord[];
  isLoading: boolean;
  error: string | null;
  importsError: string | null;
  tierLimitError: ApiError | null;
  reload(): void;
  refreshImports(): void;
  refreshEvents(): void;
  setTemplates(update: (current: HudlExportTemplate[]) => HudlExportTemplate[]): void;
}

export function useHudlPage(): HudlPageData {
  const api = useApiClient();
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [fields, setFields] = useState<HudlExportFieldCatalog | null>(null);
  const [templates, setTemplatesState] = useState<HudlExportTemplate[]>([]);
  const [imports, setImports] = useState<HudlImportRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importsError, setImportsError] = useState<string | null>(null);
  const [tierLimitError, setTierLimitError] = useState<ApiError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [importsToken, setImportsToken] = useState(0);
  const [eventsToken, setEventsToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;
    setIsLoading(true);
    setError(null);
    void (async () => {
      try {
        const [catalog, templatePage] = await Promise.all([
          api.getHudlExportFields(controller.signal),
          api.listHudlExportTemplates(controller.signal),
        ]);
        if (!isActive) return;
        setFields(catalog);
        setTemplatesState(templatePage.items);
        setTierLimitError(null);
      } catch (caught) {
        if (!isActive || controller.signal.aborted) return;
        if (ApiError.isApiError(caught) && caught.code === TIER_LIMIT_ERROR_CODE) {
          setTierLimitError(caught);
          return;
        }
        setError(toMessage(caught, 'The Hudl page could not be loaded.'));
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [api, reloadToken]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;
    void (async () => {
      try {
        const page = await api.listEvents(
          { limit: EVENT_PAGE_SIZE, sort_by: 'scheduled_at', sort_order: 'desc' },
          controller.signal
        );
        if (isActive) setEvents(sortEventsForExport(page.items));
      } catch (caught) {
        if (!isActive || controller.signal.aborted) return;
        setError(toMessage(caught, 'Your games could not be loaded.'));
      }
    })();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [api, reloadToken, eventsToken]);

  useEffect(() => {
    if (tierLimitError !== null) return undefined;
    const controller = new AbortController();
    let isActive = true;
    void (async () => {
      try {
        const page = await api.listHudlImports(RECENT_IMPORT_LIMIT, controller.signal);
        if (!isActive) return;
        setImports(page.items);
        setImportsError(null);
      } catch (caught) {
        if (!isActive || controller.signal.aborted) return;
        setImportsError(toMessage(caught, 'Recent imports could not be loaded.'));
      }
    })();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [api, reloadToken, importsToken, tierLimitError]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  const refreshImports = useCallback(() => setImportsToken((token) => token + 1), []);
  const refreshEvents = useCallback(() => setEventsToken((token) => token + 1), []);
  const setTemplates = useCallback(
    (update: (current: HudlExportTemplate[]) => HudlExportTemplate[]) => setTemplatesState(update),
    []
  );

  return {
    events,
    fields,
    templates,
    imports,
    isLoading,
    error,
    importsError,
    tierLimitError,
    reload,
    refreshImports,
    refreshEvents,
    setTemplates,
  };
}
